/**
 * The measured width of one element, for `pickTeamLabel` (Scores UX spec
 * v1.0 section 1.4): a ResizeObserver on the cell, starting from a caller's
 * estimate so the first render does not flash a different label. A cell that
 * is not laid out yet (display none, a detached test tree) keeps the
 * estimate rather than collapsing every name to its abbreviation.
 */
import { type RefObject, useLayoutEffect, useRef, useState } from "react";

export function useMeasuredWidth<T extends HTMLElement>(
  estimatePx: number,
): { ref: RefObject<T | null>; widthPx: number } {
  const ref = useRef<T | null>(null);
  const [widthPx, setWidthPx] = useState(estimatePx);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const measure = (): void => {
      const width = element.getBoundingClientRect().width;
      if (width > 0) setWidthPx(width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  return { ref, widthPx };
}

/** The current viewport width, or the content cap where there is none. */
export function viewportWidthPx(fallbackPx: number): number {
  return typeof window === "undefined" ? fallbackPx : window.innerWidth;
}
